import type Map from '@/components/map/Map.vue'
import type { LayerSpecification } from 'maplibre-gl'
import maplibregl from 'maplibre-gl'
import MapService from '@/services/map/MapService'
import { MartinStyleService } from '@/services/map/MartinStyleService'

const COMMUNE_STYLE_ID = 'commune'

/**
 * Service pour gérer la couche des communes depuis le serveur Martin.
 * Style chargé via GET /api/attributs/commune/style.
 */
export class CommuneLayerService {
  private static readonly LAYER_ID = 'commune-layer'
  private static readonly OUTLINE_LAYER_ID = 'commune-layer-outline'
  private static readonly LABELS_LAYER_ID = 'commune-layer-labels'
  private static readonly SOURCE_ID = 'commune-source'
  private static readonly SOURCE_LAYER = 'commune'

  /** Singleton promise to prevent concurrent addCommuneLayerToMap calls. */
  private static _addInFlight: Promise<void> | null = null

  /**
   * Ajoute la couche des communes à la carte (style depuis le backend).
   * @param myMap Instance de la carte MapLibre
   */
  static addCommuneLayerToMap(myMap: InstanceType<typeof Map> | undefined): Promise<void> {
    if (this._addInFlight) return this._addInFlight
    this._addInFlight = this._doAddCommuneLayer(myMap).finally(() => {
      this._addInFlight = null
    })
    return this._addInFlight
  }

  private static async _doAddCommuneLayer(myMap: InstanceType<typeof Map> | undefined): Promise<void> {
    if (!myMap?.map) {
      console.error("La carte n'est pas initialisée")
      return
    }

    const map = myMap.map

    if (!map.loaded()) {
      await new Promise<void>((resolve) => {
        map.once('load', () => resolve())
      })
    }

    if (map.getSource(this.SOURCE_ID)) {
      if (!map.getLayer(this.LABELS_LAYER_ID)) {
        const beforeId = MapService.getFirstIndicatorLayerId(map) ?? undefined
        const fondMetadata = { isPersistent: true, layerGroup: MapService.LAYER_GROUP.FOND } as const
        map.addLayer(
          {
            id: this.LABELS_LAYER_ID,
            type: 'symbol',
            source: this.SOURCE_ID,
            'source-layer': this.SOURCE_LAYER,
            minzoom: 0,
            layout: {
              'text-field': ['coalesce', ['get', 'cmn_titre'], ['get', 'nom'], ['get', 'name'], ''],
              'text-size': 11,
              'text-anchor': 'center',
              'text-allow-overlap': false,
              'text-ignore-placement': false,
              visibility: 'none',
            },
            paint: {
              'text-color': '#14532d',
              'text-halo-color': '#ffffff',
              'text-halo-width': 1.5,
            },
            metadata: fondMetadata,
          },
          beforeId,
        )
      }
      return
    }

    const style = await MartinStyleService.loadStyleFromBackend(COMMUNE_STYLE_ID)
    if (!style?.sources?.commune || !style.layers?.length) {
      console.error('[CommuneLayerService] Style commune introuvable (GET /api/attributs/commune/style)')
      return
    }

    const sourceSpec = style.sources.commune as { type: 'vector'; tiles: string[] }
    const tiles = sourceSpec?.tiles?.[0]
    if (!tiles) {
      console.error('[CommuneLayerService] Style commune sans tiles')
      return
    }

    map.addSource(this.SOURCE_ID, {
      type: 'vector',
      tiles: [tiles],
      minzoom: 0,
      maxzoom: 22,
      promoteId: 'cmn_code',
    })

    const layersForCommune = (style.layers as LayerSpecification[]).filter(
      (l) => 'source' in l && l.source === 'commune' && (l.type === 'fill' || l.type === 'line'),
    )
    const fillLayerSpec = layersForCommune.find((l) => l.type === 'fill')
    const outlineLayerSpec = layersForCommune.find((l) => l.type === 'line')
    const beforeId = MapService.getFirstIndicatorLayerId(map) ?? undefined
    const fondMetadata = { isPersistent: true, layerGroup: MapService.LAYER_GROUP.FOND } as const

    if (fillLayerSpec) {
      const fillPaint = fillLayerSpec.paint as Record<string, unknown> | undefined
      const defaultFillColor =
        (typeof fillPaint?.['fill-color'] === 'string' ? fillPaint['fill-color'] : null) ?? '#e5e7eb'
      const defaultFillOpacity =
        typeof fillPaint?.['fill-opacity'] === 'number' ? fillPaint['fill-opacity'] : 0.25

      const fillLayer: maplibregl.LayerSpecification = {
        ...JSON.parse(JSON.stringify(fillLayerSpec)),
        id: this.LAYER_ID,
        source: this.SOURCE_ID,
        layout: { ...(fillLayerSpec.layout as object), visibility: 'none' },
        paint: {
          ...(fillPaint as object),
          'fill-color': [
            'case',
            ['any', ['boolean', ['feature-state', 'hover'], false], ['boolean', ['feature-state', 'selected'], false]],
            'rgba(2, 89, 89, 0.8)',
            defaultFillColor,
          ],
          'fill-opacity': [
            'case',
            ['any', ['boolean', ['feature-state', 'hover'], false], ['boolean', ['feature-state', 'selected'], false]],
            0.8,
            defaultFillOpacity,
          ],
        },
        metadata: fondMetadata,
      }
      map.addLayer(fillLayer, beforeId)
    }

    if (outlineLayerSpec) {
      const outlinePaint = outlineLayerSpec.paint as Record<string, unknown> | undefined
      const defaultLineColor =
        (typeof outlinePaint?.['line-color'] === 'string' ? outlinePaint['line-color'] : null) ?? '#6b7280'
      const defaultLineWidth =
        typeof outlinePaint?.['line-width'] === 'number' ? outlinePaint['line-width'] : 1

      const outlineLayer: maplibregl.LayerSpecification = {
        ...JSON.parse(JSON.stringify(outlineLayerSpec)),
        id: this.OUTLINE_LAYER_ID,
        source: this.SOURCE_ID,
        layout: { ...(outlineLayerSpec.layout as object), visibility: 'none' },
        paint: {
          ...(outlinePaint as object),
          'line-color': [
            'case',
            ['any', ['boolean', ['feature-state', 'hover'], false], ['boolean', ['feature-state', 'selected'], false]],
            'rgba(2, 89, 89, 1)',
            defaultLineColor,
          ],
          'line-width': [
            'case',
            ['any', ['boolean', ['feature-state', 'hover'], false], ['boolean', ['feature-state', 'selected'], false]],
            Math.max(defaultLineWidth * 2.5, 2.5),
            defaultLineWidth,
          ],
        },
        metadata: fondMetadata,
      }
      map.addLayer(outlineLayer, beforeId)
    }

    if (!map.getLayer(this.LABELS_LAYER_ID)) {
      map.addLayer(
        {
          id: this.LABELS_LAYER_ID,
          type: 'symbol',
          source: this.SOURCE_ID,
          'source-layer': this.SOURCE_LAYER,
          minzoom: 0,
          layout: {
            'text-field': ['coalesce', ['get', 'cmn_titre'], ['get', 'nom'], ['get', 'name'], ''],
            'text-size': 11,
            'text-anchor': 'center',
            'text-allow-overlap': false,
            'text-ignore-placement': false,
            visibility: 'none',
          },
          paint: {
            'text-color': '#14532d',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.5,
          },
          metadata: fondMetadata,
        },
        beforeId,
      )
    }

    console.log('[CommuneLayerService] Couche commune ajoutée à la carte')
  }

  /**
   * Supprime la couche des communes de la carte
   * @param myMap Instance de la carte MapLibre
   */
  static removeCommuneLayerFromMap(myMap: InstanceType<typeof Map> | undefined): void {
    if (!myMap?.map) {
      return
    }

    const map = myMap.map

    // Nettoyer les listeners et les états
    this.cleanup(map)

    // Supprimer les couches (labels, outline, fill)
    if (map.getLayer(this.LABELS_LAYER_ID)) {
      map.removeLayer(this.LABELS_LAYER_ID)
    }

    if (map.getLayer(this.LAYER_ID)) {
      map.removeLayer(this.LAYER_ID)
    }

    if (map.getLayer(this.OUTLINE_LAYER_ID)) {
      map.removeLayer(this.OUTLINE_LAYER_ID)
    }

    // Supprimer la source
    if (map.getSource(this.SOURCE_ID)) {
      map.removeSource(this.SOURCE_ID)
    }
  }

  /**
   * Active ou désactive la visibilité de la couche des communes
   * @param myMap Instance de la carte MapLibre
   * @param visible true pour afficher, false pour masquer
   */
  static setCommuneLayerVisibility(
    myMap: InstanceType<typeof Map> | undefined,
    visible: boolean,
  ): void {
    if (!myMap?.map) {
      return
    }

    const map = myMap.map
    const visibility = visible ? 'visible' : 'none'

    if (map.getLayer(this.LAYER_ID)) {
      map.setLayoutProperty(this.LAYER_ID, 'visibility', visibility)
    }

    if (map.getLayer(this.OUTLINE_LAYER_ID)) {
      map.setLayoutProperty(this.OUTLINE_LAYER_ID, 'visibility', visibility)
    }

    if (map.getLayer(this.LABELS_LAYER_ID)) {
      map.setLayoutProperty(this.LABELS_LAYER_ID, 'visibility', visibility)
    }
  }

  /**
   * Retourne l'ID de la couche des communes
   */
  static getLayerId(): string {
    return this.LAYER_ID
  }

  /**
   * Retourne l'ID de la couche de contour des communes
   */
  static getOutlineLayerId(): string {
    return this.OUTLINE_LAYER_ID
  }

  /**
   * Retourne l'ID de la couche de labels des communes
   */
  static getLabelsLayerId(): string {
    return this.LABELS_LAYER_ID
  }

  /**
   * Retourne l'ID de la source des communes
   */
  static getSourceId(): string {
    return this.SOURCE_ID
  }

  /**
   * Met en surbrillance un polygone au survol
   * @param map Instance de la carte MapLibre
   * @param featureId ID de la feature à mettre en surbrillance
   */
  static highlightFeature(map: maplibregl.Map, featureId: string | number | undefined): void {
    if (!featureId || !map.getLayer(this.LAYER_ID)) return

    try {
      const featureState = { source: this.SOURCE_ID, sourceLayer: 'commune', id: featureId }
      map.setFeatureState(featureState, { hover: true })
      console.log('[CommuneLayerService] Feature highlighted:', featureId, 'State set:', {
        hover: true,
      })
    } catch (error) {
      console.error('[CommuneLayerService] Error highlighting feature:', error, { featureId })
    }
  }

  /**
   * Retire la surbrillance d'un polygone
   * @param map Instance de la carte MapLibre
   * @param featureId ID de la feature
   */
  static unhighlightFeature(map: maplibregl.Map, featureId: string | number | undefined): void {
    if (!featureId || !map.getLayer(this.LAYER_ID)) return

    try {
      map.setFeatureState(
        { source: this.SOURCE_ID, sourceLayer: 'commune', id: featureId },
        { hover: false },
      )
    } catch (error) {
      console.error('[CommuneLayerService] Error unhighlighting feature:', error, { featureId })
    }
  }

  private static selectedFeatureId: string | number | null = null
  private static dimmedFeatureIds: Set<string | number> = new Set()
  private static mapInstance: maplibregl.Map | null = null
  private static sourceDataHandler: ((e: maplibregl.MapSourceDataEvent) => void) | null = null

  /**
   * Initialise le listener pour les nouvelles tuiles chargées
   * @param map Instance de la carte MapLibre
   */
  private static setupSourceDataListener(map: maplibregl.Map): void {
    if (this.mapInstance === map && this.sourceDataHandler) {
      return // Déjà configuré
    }

    // Nettoyer l'ancien listener si on change de carte
    if (this.mapInstance && this.sourceDataHandler) {
      this.mapInstance.off('sourcedata', this.sourceDataHandler)
    }

    this.mapInstance = map
    this.sourceDataHandler = (e: maplibregl.MapSourceDataEvent) => {
      // Quand de nouvelles données sont chargées pour la source des communes
      if (e.sourceId === this.SOURCE_ID && e.isSourceLoaded) {
        // Si une feature est sélectionnée, obscurcir toutes les nouvelles features chargées
        if (this.selectedFeatureId !== null) {
          this.dimAllLoadedFeatures(map)
        }
      }
    }

    map.on('sourcedata', this.sourceDataHandler)
  }

  /**
   * Obscurcit toutes les features actuellement chargées dans la source
   * @param map Instance de la carte MapLibre
   */
  private static dimAllLoadedFeatures(map: maplibregl.Map): void {
    if (!map.getLayer(this.LAYER_ID) || this.selectedFeatureId === null) return

    try {
      // Utiliser querySourceFeatures pour obtenir toutes les features chargées de la source
      // Note: querySourceFeatures fonctionne avec les tuiles vectorielles et retourne toutes les features chargées
      const features = map.querySourceFeatures(this.SOURCE_ID, {
        sourceLayer: 'commune',
      })

      features.forEach((feature) => {
        if (feature.id !== undefined && feature.id !== this.selectedFeatureId) {
          const id = feature.id
          // Ne marquer que si pas déjà dans le set (évite les appels inutiles)
          if (!this.dimmedFeatureIds.has(id)) {
            this.dimmedFeatureIds.add(id)
            map.setFeatureState(
              { source: this.SOURCE_ID, sourceLayer: 'commune', id },
              { dimmed: true },
            )
          }
        }
      })
    } catch (error) {
      console.error("[CommuneLayerService] Erreur lors de l'obscurcissement des features:", error)
    }
  }

  /**
   * Sélectionne une commune et obscurcit les autres
   * @param map Instance de la carte MapLibre
   * @param featureId ID de la feature sélectionnée
   */
  static selectFeature(map: maplibregl.Map, featureId: string | number | undefined): void {
    if (!map.getLayer(this.LAYER_ID) || !featureId) return

    // Configurer le listener pour les nouvelles tuiles si ce n'est pas déjà fait
    this.setupSourceDataListener(map)

    // D'abord, restaurer toutes les features précédemment obscurcies
    this.dimmedFeatureIds.forEach((id) => {
      map.setFeatureState({ source: this.SOURCE_ID, sourceLayer: 'commune', id }, { dimmed: false })
    })
    this.dimmedFeatureIds.clear()

    // Désélectionner la feature précédente si elle existe
    if (this.selectedFeatureId !== null && this.selectedFeatureId !== featureId) {
      map.setFeatureState(
        { source: this.SOURCE_ID, sourceLayer: 'commune', id: this.selectedFeatureId },
        { selected: false },
      )
    }

    // Marquer la nouvelle feature comme sélectionnée
    this.selectedFeatureId = featureId
    map.setFeatureState(
      { source: this.SOURCE_ID, sourceLayer: 'commune', id: featureId },
      { selected: true, dimmed: false },
    )

    // Obscurcir toutes les features actuellement chargées (pas seulement celles visibles)
    this.dimAllLoadedFeatures(map)
  }

  /**
   * Désélectionne toutes les communes et restaure le style normal
   * @param map Instance de la carte MapLibre
   */
  static deselectAllFeatures(map: maplibregl.Map): void {
    if (!map.getLayer(this.LAYER_ID)) return

    // Désélectionner la feature sélectionnée
    if (this.selectedFeatureId !== null) {
      map.setFeatureState(
        { source: this.SOURCE_ID, sourceLayer: 'commune', id: this.selectedFeatureId },
        { selected: false },
      )
      this.selectedFeatureId = null
    }

    // Restaurer toutes les features obscurcies
    this.dimmedFeatureIds.forEach((id) => {
      map.setFeatureState({ source: this.SOURCE_ID, sourceLayer: 'commune', id }, { dimmed: false })
    })
    this.dimmedFeatureIds.clear()
  }

  /**
   * Nettoie les listeners et les références lors de la suppression de la couche
   * @param map Instance de la carte MapLibre
   */
  static cleanup(map: maplibregl.Map): void {
    if (this.mapInstance === map && this.sourceDataHandler) {
      map.off('sourcedata', this.sourceDataHandler)
      this.sourceDataHandler = null
      this.mapInstance = null
    }
    this.selectedFeatureId = null
    this.dimmedFeatureIds.clear()
  }
}
